<template>
  <tr v-bind:class="sampleRowClass">
    <td>
      <v-tooltip bottom v-if="!formValid">
        <template v-slot:activator="{ on }">
          <i class="fas fa-exclamation not-valid" v-on="on"></i>
        </template>
        <span>Sample contains invalid data</span>
      </v-tooltip>
      <v-checkbox class="checkbox-no-margins"
                  primary
                  hide-details
                  v-model="sampleTarget"
                  @change="handleTargetChange"
                  :indeterminate="isMissing"
                  @mousedown="handleSampleTargetCheckBoxClick"
      ></v-checkbox>
      <confirm ref="targetConfirm" persistent></confirm>
    </td>
    <td>
      <span>{{ data.title }}</span>
      <v-tooltip bottom v-if="data.status === sampleStatuses.Sent">
        <template v-slot:activator="{ on }">
          <i class="fas fa-flag-checkered ml-1" v-on="on"></i>
        </template>
        <span>Sample analysis complete</span>
      </v-tooltip>
      <v-tooltip bottom v-if="data.clientComment">
        <template v-slot:activator="{ on }">
          <v-icon x-small class="ml-1" v-on="on">fas fa-comment-alt</v-icon>
        </template>
        <span>Комментарий от клиники: {{ data.clientComment }}</span>
      </v-tooltip>
    </td>
    <td class="application-date-cell">
      <div class="application-date">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ reformatDate(applicationDate) | NoEmpty }}</span>
          </template>
          <span>Application date</span>
        </v-tooltip>
      </div>

      <div class="period-execution">
        <v-tooltip bottom v-if="data.priority">
          <template v-slot:activator="{ on }">
            <i class="fas fa-bolt mr-1 high-priority" v-on="on"></i>
          </template>
          <span>High priority sample</span>
        </v-tooltip>
        {{ periodExecutionFormatted }}
      </div>
      <div class="estimated-completion-datetime">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ reformatDate(estimatedCompletionDatetimeFormatted) | NoEmpty }}</span>
          </template>
          <span>Estimated deadline</span>
        </v-tooltip>
        <v-progress-circular v-if="calculateEstimatedCompletionDatetimeInProgress" class="estimated-completion-progress"
                             width="1" size="8"
                             color="primary" indeterminate></v-progress-circular>
      </div>
    </td>

    <td>
      {{ sampleFertilizationMethodFormatted }}
    </td>
    <td>
      <div v-if="inBatch">
        <div>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon x-small v-on="on">fas fa-vial</v-icon>
              <span class="plate-status-info"> {{ data.batchSystemId }} ({{ data.positionInBatch }})</span>
            </template>
            <span>Position in plate</span>
          </v-tooltip>
        </div>
        <div v-if="responsibleSpecialistName !== null">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon x-small v-on="on">fas fa-user</v-icon>
              <span class="plate-status-small-info"> {{ responsibleSpecialistName }}</span>
            </template>
            <span>Responsible specialist</span>
          </v-tooltip>
        </div>
        <div v-if="data.batchSeqStartDatetime !== null">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon x-small v-on="on">fas fa-calendar</v-icon>
              <span class="plate-status-small-info"> {{ data.batchSeqStartDatetime | DateWithMinutes }}</span>
            </template>
            <span>Plate start processing date</span>
          </v-tooltip>
        </div>
      </div>
      <div v-if="data.target === sampleTargets.Analysis && !inBatch">
        Suitable plate: <span class="suitable-batch-system-id">{{ suitableBatchSystemId }}</span>
      </div>
    </td>
    <td>
      <div class="buttons-wrapper">
        <v-btn icon small @click="handleEditButtonClick" class="no-margin">
          <v-icon small>fas fa-pencil-alt</v-icon>
        </v-btn>
        <v-btn icon small @click="handleDeleteButtonClick" class="no-margin" v-if="canDelete">
          <v-icon small>fas fa-trash</v-icon>
        </v-btn>
      </div>
      <div>
        <v-menu
            v-model="showSampleEditMenu"
            :position-x="sampleEditMenuX"
            :position-y="sampleEditMenuY"
            :close-on-content-click="false"
            absolute
            offset-y
            max-width="500"
        >
          <v-card class="edit-sample-wrapper">
            <v-card-title>Edit sample</v-card-title>
            <v-card-text class="text--primary">
              <div>
                <v-text-field ref="titleInput"
                              v-model="data.title"
                              label="Title"
                              :rules="titleRules"
                              :counter="50"
                              @input="handleInputTitle"
                              @change="handleChangeTitle"></v-text-field>
              </div>
              <div>
                <v-menu
                    v-model="applicationDatetimeMenu"
                    :close-on-content-click="false"
                    :disabled="isApplicationDateDisabled"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                        :value="reformatDate(applicationDate)"
                        :disabled="isApplicationDateDisabled"
                        label="Application date"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="applicationDate"
                                 @input="handleApplicationDatetimeChange"
                                 first-day-of-week="1"></v-date-picker>
                </v-menu>
              </div>
              <div>
                <period-execution-selector class="case-form-item"
                                           ref="periodExecutionSelector"
                                           :period-execution="data.periodExecution"
                                           :available-periods="availableExecutionPeriods"
                                           @changed="handlePeriodExecutionChanged"
                                           @validation-changed="handlePeriodExecutionSelectorValidationChange"></period-execution-selector>
              </div>
              <div>
                <v-select
                    :items="availableSampleDaysTypes"
                    v-model="data.daysType"
                    item-value="value"
                    item-text="name"
                    label="Days type"
                    persistent-hint
                    @change="handleDaysTypeChange"
                    required
                ></v-select>
              </div>
              <div>
                <v-select
                    :items="availableEmbryologists"
                    v-model="data.biopsyEmbryologist"
                    item-value="id"
                    item-text="name"
                    label="Biopsy embryologist"
                    persistent-hint
                    required
                ></v-select>
              </div>
              <div>
                <v-radio-group v-model="data.fertilizationMethod"
                               ref="fertilizationMethodSelector"
                               row
                               :mandatory="false"
                               class="small"
                               :rules="fertilizationMethodRules"
                               @change="handleFertilizationMethodSelectorChange">
                  <v-radio label="ЭКО" value="ivf"></v-radio>
                  <v-radio label="ИКСИ" value="icsi"></v-radio>
                </v-radio-group>
                <v-checkbox v-model="data.priority" label="Priority" small>
                </v-checkbox>
              </div>
              <div v-if="devMode">
                <v-btn x-small color="error" @click="handleResetDueDaysCounterClick">Reset due days counter</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </td>
  </tr>
</template>

<script>

import Confirm from '../Common/Confirm'
import PeriodExecutionSelector from '../Common/PeriodExecutionSelector'
import {isNumeric} from '@/helpers/numbers'
import _ from 'lodash'
import moment from 'moment'
import * as SampleTargets from '../../dictionaries/sample-targets'
import * as SampleStatuses from '../../dictionaries/sample-statuses'
import * as SampleFertilizationMethods from '../../dictionaries/sample-fertilization-methods'
import * as SampleDaysTypes from '../../dictionaries/sample-days-types'
import {mapActions, mapGetters, mapState} from 'vuex'
import {periodExecutionToDays, reformatDate} from '@/helpers/timeformats'
import NoEmpty from '../Filters/NoEmpty'
import DateWithMinutes from '../Filters/DateWithMinutes'
import commonApi from '../../api'
import NotificationService from '../../services/notificationService'
import {SampleTitleRegex} from '@/dictionaries/common-rules'
import {clientsById, clientSpecialistsById} from "@/store/modules/clients/getters/common";

export default {
  name: 'Item',
  props: {
    data: Object,
    sampleExistChecker: Function,
    suitableBatch: Object,
    selectedClientId: String,
  },
  components: {
    Confirm,
    PeriodExecutionSelector
  },
  filters: {
    NoEmpty,
    DateWithMinutes
  },
  data() {
    const periodExecution = _.get(this.data, 'periodExecution', null)

    return {
      titleInputValid: true,
      samplePeriodExecutionValid: periodExecution !== null,
      fertilizationMethodValid: !!this.data.fertilizationMethod,
      calculateEstimatedCompletionDatetimeInProgress: false,
      applicationDatetimeMenu: false,
      showSampleEditMenu: false,
      sampleEditMenuX: 0,
      sampleEditMenuY: 0,
      formValid: false,
      titleRules: [
        v => !!v || 'Enter title',
        function (v) {
          return SampleTitleRegex.test(v) || 'Wrong symbols'
        },
        function (v) {
          return (v && v.length <= 50) || 'The length of the title should be no more than 50 characters'
        },
        (v) => {
          return this.sampleExistChecker(this.data, v) || "Title is already exists"
        }
      ],
      fertilizationMethodRules: [
        v => !!v || 'Select fertilization method',
      ],
      sampleTargets: SampleTargets,
      sampleStatuses: SampleStatuses,
      calculateEstimateCompletionDatetime: _.debounce(async function () {
        if (!this.data.applicationDatetime) {
          return
        }

        this.calculateEstimatedCompletionDatetimeInProgress = true

        const days = parseInt(this.data.periodExecution / 24)

        let date = null
        try {
          if (this.data.daysType === SampleDaysTypes.Calendar) {
            date = await commonApi.getEndPeriod(this.data.applicationDatetime, days, SampleDaysTypes.Calendar)
          } else {
            date = await commonApi.getEndPeriod(this.data.applicationDatetime, days, SampleDaysTypes.Work)
          }
        } catch (e) {
          NotificationService.error('Unable to calculate days. Please try again later.')
          return
        } finally {
          this.calculateEstimatedCompletionDatetimeInProgress = false
        }

        this.data.estimatedCompletionDatetime = date

      }, 300)
    }
  },
  watch: {
    'data.fertilizationMethod': function(newValue) {
      this.fertilizationMethodValid = !!newValue

      this.validate()
    },
    data(newValue) {
      this.fertilizationMethodValid = !!newValue.fertilizationMethod

      this.validate()
    },
    showSampleEditMenu(newValue) {
      if (!newValue) {
        this.$emit('close-sample-edit-menu')
      }
    }
  },
  computed: {
    ...mapGetters({
      availableSpecialistsByIds: 'availableSpecialistsByIds'
    }),
    ...mapState({
      devMode: 'devMode',
      availableExecutionPeriods: 'availableExecutionPeriods',
      availableSampleDaysTypes: 'availableSampleDaysTypes'
    }),
    ...mapGetters('clients', {
      clientsById: 'clientsById'
    }),
    availableEmbryologists() {
      return _.get(this.clientsById, [this.selectedClientId, 'specialists'], [])
    },
    responsibleSpecialistName() {
      return _.get(this.availableSpecialistsByIds, [this.data.batchResponsibleSpecialistId, 'name'], null)
    },
    isApplicationDateDisabled() {
      return this.data.status !== SampleStatuses.Missing && this.data.target !== SampleTargets.Analysis
    },
    sampleFertilizationMethodFormatted() {
      switch (this.data.fertilizationMethod) {
        case SampleFertilizationMethods.Ivf:
          return 'ЭКО'
        case SampleFertilizationMethods.Icsi:
          return 'ИКСИ'
        default:
          return '-'
      }
    },
    applicationDate: {
      get() {
        if (!this.data.applicationDatetime) {
          return null
        }

        return moment(this.data.applicationDatetime).format('YYYY-MM-DD')
      },
      set(value) {
        this.data.applicationDatetime = moment(value).set({
          hour: 12,
          minute: 0,
          second: 0,
          millisecond: 0
        }).toISOString()
      }
    },
    sampleTarget: {
      get() {
        if (this.data.target === SampleTargets.Analysis) {
          return true
        }

        return false
      },
      set(value) {
        if (value) {
          this.data.target = SampleTargets.Analysis

          if (this.data.applicationDatetime == null) {
            // если произошла установка образца на анализ, то устанавливаем время получения заявки
            this.data.applicationDatetime = moment().set({
              hour: 12,
              minute: 0,
              second: 0,
              millisecond: 0
            }).toISOString()
          }

          this.calculateEstimateCompletionDatetime()
          return
        }

        this.data.target = SampleTargets.Archive
      }
    },
    estimatedCompletionDatetimeFormatted() {
      if (!this.data.estimatedCompletionDatetime) {
        return null
      }

      return moment(this.data.estimatedCompletionDatetime).format('YYYY-MM-DD')
    },
    periodExecutionFormatted() {
      let daysTypeFormatted = 'WD'

      if (this.data.daysType === SampleDaysTypes.Calendar) {
        daysTypeFormatted = 'CD'
      }

      const daysCount = periodExecutionToDays(this.data.periodExecution)

      return `${daysCount}${daysTypeFormatted}`
    },
    isMissing() {
      return this.data.status === SampleStatuses.Missing && !this.data.targetChanged
    },
    inBatch() {
      return this.data.batchId != null
    },
    canDelete() {
      return !this.inBatch
    },
    suitableBatchSystemId() {
      return _.get(this.suitableBatch, ['batch', 'systemId'], '-')
    },
    sampleRowClass() {
      return {
        'sample-missing': this.isMissing
      }
    }
  },
  mounted() {
    if (!this.data.id && this.data.applicationDatetime !== null) {
      this.calculateEstimateCompletionDatetime()
    }
    // this.formValid = this.validate(false)
  },
  methods: {
    ...mapActions('pgtCases', {
      resetSampleDueDaysCounter: 'resetSampleDueDaysCounter'
    }),
    handleDeleteButtonClick() {
      this.$emit('delete', _.get(this.data, 'tempId', _.get(this.data, 'id')))
    },
    handleEditButtonClick(e) {
      this.sampleEditMenuX = e.clientX
      this.sampleEditMenuY = e.clientY
      this.$nextTick(() => {
        this.showSampleEditMenu = true
      })
    },
    handleInputTitle() {
      this.validate()
    },
    handleChangeTitle() {
      this.$emit('change', this.data)
    },
    handleFertilizationMethodSelectorChange() {
      this.validate()
    },
    handleTargetChange(value) {
      this.$emit('target-changed', this.data.target)
    },
    handlePeriodExecutionChanged(value) {
      if (!this.$refs['periodExecutionSelector']) {
        return
      }

      if (!this.$refs['periodExecutionSelector'].validate(true)) {
        return
      }

      this.data.periodExecution = parseInt(value)
      this.calculateEstimateCompletionDatetime()
    },
    handlePeriodExecutionSelectorValidationChange() {
      this.validate()
    },
    handleDaysTypeChange() {
      this.calculateEstimateCompletionDatetime()
    },
    handleApplicationDatetimeChange() {
      this.applicationDatetimeMenu = false
      this.calculateEstimateCompletionDatetime()
    },
    validate(silent = false) {
      let isTitleInputValid = this.titleInputValid
      let isPeriodExecutionFormValid = this.samplePeriodExecutionValid
      let isFertilizationMethodValid = this.fertilizationMethodValid

      if (this.$refs['titleInput'] && this.$refs['periodExecutionSelector'] && this.$refs['fertilizationMethodSelector']) {
        isTitleInputValid = this.$refs['titleInput'].validate(true)
        this.titleInputValid = isTitleInputValid

        isPeriodExecutionFormValid = this.$refs['periodExecutionSelector'].validate(true)
        this.samplePeriodExecutionValid = isPeriodExecutionFormValid

        isFertilizationMethodValid = this.$refs['fertilizationMethodSelector'].validate(true)
        this.fertilizationMethodValid = isFertilizationMethodValid
      }

      const validationResult = isTitleInputValid && isPeriodExecutionFormValid && isFertilizationMethodValid

      if (this.formValid !== validationResult) {
        this.formValid = validationResult

        if (!silent) {
          this.$emit('validation-changed', validationResult)
        }
      }

      return validationResult
    },
    async handleSampleTargetCheckBoxClick(e) {
      if (this.data.target !== SampleTargets.Analysis) {
        this.data.targetChanged = true
        return true
      }

      this.$refs.targetConfirm.open('This will change status of sample to ‘Archive’', 'Are you sure?', {width: 350}).then(async (confirm) => {
        if (!confirm) {
          return
        }
        this.data.target = SampleTargets.Archive

        // если образец отправили в архив, то сбрасываем время получения заявки
        this.data.applicationDatetime = null
        this.data.estimatedCompletionDatetime = null
        this.data.targetChanged = true
      })

      return false
    },
    async handleResetDueDaysCounterClick() {
      await this.resetSampleDueDaysCounter(this.data.id)
    },
    reformatDate: reformatDate
  }
}
</script>

<style scoped>
.no-margin {
  margin: 0
}

.suitable-batch-system-id {
  color: #dab45c;
}

.sample-missing {
  background-color: #ffe4e4;
}

.buttons-wrapper {
  min-width: 100px;
}

.not-valid {
  position: absolute;
  left: 5px;
  margin-top: 4px;
  font-size: 16px;
  color: #ff5252;
}

.application-date {
  font-size: 9px;
}

.estimated-completion-datetime {
  font-size: 9px;
}

.period-execution {
  font-weight: 500;
}

.application-date-cell {
  text-align: center;
}

.estimated-completion-progress {
  position: absolute;
  margin-top: 2px;
  margin-left: 3px;
}

.high-priority {
  color: red;
}

.plate-status-info {
  font-size: 12px;
}

.plate-status-small-info {
  font-size: 10px;
}

</style>

<style>
.small .v-radio {
  padding: 0px;
}

.small i {
  font-size: 16px !important;
}

.small label {
  padding-left: 0px;
  font-size: 12px
}

.small [class*="__ripple"] {
  left: -13px;
}

.small {
  margin-top: 12px !important;
}

.small .v-input__slot {
  margin-bottom: 0 !important;
}

.small .v-input--selection-controls__input {
  margin-right: -2px;
}

.small .v-radio {
  margin-right: 10px;
}

.edit-sample-wrapper .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.checkbox-no-margins {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>